import { Fragment, memo } from "react";
import { IIssueRatingCategory } from "types";
import { Card } from "components";
import { BorderBadge } from "components/BorderBadge";
import classnames from "classnames";
import Avatar from "react-avatar";
import { ReactComponent as RatingStar } from "assets/images/rating-star.svg";
import { LocationMarkerIcon } from "@heroicons/react/outline";

interface IProps {
  title: string;
  location?: string;
  profileImage: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  createdAt: string | undefined;
  id: string | undefined;
  rating: number;
  email: string | undefined;
  forceHoverIssue?: boolean;
  issueType: "regular" | "city" | "country";
  setHoveredIssueType: React.Dispatch<
    React.SetStateAction<"regular" | "city" | "country" | undefined>
  >;
  setIsIssueHovered?: React.Dispatch<React.SetStateAction<boolean>>;
  setIssueHoveredProps?: React.Dispatch<
    React.SetStateAction<{
      lat: number;
      lng: number;
    }>
  >;
  geoLocation: {
    lat: number | undefined;
    lng: number | undefined;
  };
  primaryClassification: Partial<IIssueRatingCategory> | undefined;
  secondaryClassification: Partial<IIssueRatingCategory> | undefined;
  innerRef?: (node?: Element | null | undefined) => void;
  score: number | undefined;
  tags?: string[] | undefined;
  isSoftDeleted?: boolean;
}

function IssueCard({
  title,
  location,
  profileImage,
  firstName = "",
  lastName = "",
  createdAt = "",
  rating,
  id,
  issueType,
  setHoveredIssueType,
  setIsIssueHovered,
  setIssueHoveredProps,
  forceHoverIssue,
  geoLocation,
  primaryClassification,
  secondaryClassification,
  email,
  score,
  innerRef,
  tags,
  isSoftDeleted,
}: IProps) {
  const date =
    createdAt &&
    new Date(createdAt).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });

  const fullName = `${firstName} ${lastName}`.trim() || email || "Anonymous";
  const getRating = (val: number) => {
    return val % 1 === 0 ? val : val.toFixed(1);
  };

  return (
    <Card
      className={classnames("maxmd:flex-1 !gap-3 !py-5 h-full", {
        "bg-gray-100 pointer-events-none select-none": isSoftDeleted,
      })}
      issueType={issueType}
      setHoveredIssueType={setHoveredIssueType}
      forceHoverIssue={forceHoverIssue}
      setIsIssueHovered={setIsIssueHovered}
      setIssueHoveredProps={setIssueHoveredProps}
      geoLocation={geoLocation}
      showHeaderDivider={false}
      showSubHeaderDivider={false}
      showContent={false}
      showFooterDivider={true}
      header={
        <Fragment>
          {/* Title - Prominent */}
          <h3
            className="font-semibold text-base leading-6 line-clamp-2 mb-2"
            ref={innerRef}
            style={{
              color: "#1C1917",
            }}
          >
            {title}
          </h3>

          {/* Category Badges - Horizontal row */}
          <div className="flex flex-wrap items-center gap-2">
            {primaryClassification && (
              <BorderBadge
                classificationName={primaryClassification.title}
                classificationDescription={primaryClassification.description}
                tooltipId={id}
              >
                {primaryClassification.title}
              </BorderBadge>
            )}
            {secondaryClassification && (
              <BorderBadge
                classificationName={secondaryClassification.title}
                classificationDescription={secondaryClassification.description}
                tooltipId={id}
              >
                {secondaryClassification.title}
              </BorderBadge>
            )}
            {!primaryClassification && !secondaryClassification && (
              <span
                className="text-xs text-stone-400 italic"
                style={{ fontFamily: "'Source Sans 3', sans-serif" }}
              >
                No classifications yet
              </span>
            )}
          </div>
        </Fragment>
      }
      classNames={{
        header: "!gap-0 !pt-0",
        subHeader: "hidden",
        content: "flex-grow",
        footer: "!pt-3 !pb-0",
      }}
      footer={
        <div className="flex flex-col gap-2 w-full">
          {/* Author row */}
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 shrink-0 rounded-full overflow-hidden border border-stone-200">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <Avatar size="28" round color="#D6D3D1" name={fullName} />
              )}
            </div>
            <span
              className="text-sm text-stone-600 font-medium truncate"
              style={{ fontFamily: "'Source Sans 3', sans-serif" }}
            >
              {fullName}
            </span>
            <span className="text-stone-300">•</span>
            <span
              className="text-xs text-stone-400"
              style={{ fontFamily: "'Source Sans 3', sans-serif" }}
            >
              {date}
            </span>
          </div>

          {/* Location & Rating row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 text-stone-500">
              <LocationMarkerIcon className="h-3.5 w-3.5" />
              <span
                className="text-xs truncate max-w-[180px]"
                style={{ fontFamily: "'Source Sans 3', sans-serif" }}
              >
                {location || "Location not set"}
              </span>
            </div>
            {rating > 0 && (
              <div
                className="flex items-center gap-1 px-2 py-0.5 rounded-full"
                style={{ background: "#F5F5F4" }}
              >
                <span
                  className="text-xs font-medium text-stone-600"
                  style={{ fontFamily: "'Source Sans 3', sans-serif" }}
                >
                  {getRating(rating)}
                </span>
                <RatingStar height={10} width={10} fill="#C2410C" />
              </div>
            )}
          </div>
        </div>
      }
      tags={
        tags &&
        tags?.length > 0 && (
          <div className="flex items-center flex-wrap gap-1.5 px-6 pt-2">
            {tags?.map((tag) => (
              <BorderBadge
                key={tag}
                classificationName={tag}
                variant="tag"
              >
                {tag}
              </BorderBadge>
            ))}
          </div>
        )
      }
    />
  );
}

export default memo(IssueCard);
